import React, { useState, useEffect } from 'react';
import { useWatchlist } from '../../contexts/WatchlistContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { ScannerData } from '../../types/ScannerData';

interface WatchlistManagerProps {
  data?: ScannerData[];
  onClose?: () => void;
}

const WatchlistManager: React.FC<WatchlistManagerProps> = ({ data, onClose }) => {
  const { watchlist, aiAnalysisEnabled, analysisLanguage, addToWatchlist, removeFromWatchlist, toggleAiAnalysis, setAnalysisLanguage } = useWatchlist();
  const { language } = useLanguage();
  const [availableSymbols, setAvailableSymbols] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (data) {
      const symbols = Array.from(new Set(data.map(d => d.Underlying || d.Symbol?.split(' ')[0]).filter(Boolean)));
      setAvailableSymbols(symbols.sort());
    }
  }, [data]);

  const filteredSymbols = availableSymbols.filter(symbol =>
    symbol.toUpperCase().includes(searchTerm.toUpperCase()) &&
    !watchlist.includes(symbol.toUpperCase())
  );

  const handleAddSymbol = (symbol: string) => {
    addToWatchlist(symbol);
    setSearchTerm('');
  };

  return (
    <div
      style={{
        padding: '16px',
        backgroundColor: 'var(--elev)',
        color: 'var(--txt)',
        border: '1px solid var(--hair)',
        borderRadius: '10px',
        fontFamily: 'var(--font-ui)',
        fontSize: '13px',
        minWidth: '400px',
        maxWidth: '600px'
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: 'var(--txt)' }}>
          {language === 'ar' ? 'قائمة المراقبة' : 'AI Chatbot Watchlist'}
        </h3>
        {onClose && (
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--muted)',
              fontSize: '18px',
              lineHeight: 1,
              cursor: 'pointer',
              padding: '0 8px'
            }}
          >
            ×
          </button>
        )}
      </div>

      {/* AI Analysis Toggle */}
      <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: 'var(--elev2)', border: '1px solid var(--hair)', borderRadius: '10px' }}>
        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginBottom: '12px', fontSize: '13px' }}>
          <input
            type="checkbox"
            checked={aiAnalysisEnabled}
            onChange={toggleAiAnalysis}
            style={{ marginRight: '8px', width: '16px', height: '16px' }}
          />
          <span>{language === 'ar' ? 'تفعيل التحليل التلقائي' : 'Enable AI Analysis'}</span>
        </label>
        <p style={{ margin: '8px 0 12px 0', fontSize: '12px', color: 'var(--muted)' }}>
          {language === 'ar'
            ? 'سيتم إرسال إشعارات تلقائية عند تحديث البيانات'
            : 'Automatic notifications will be sent when data updates'}
        </p>
        
        {/* Analysis Language Selector */}
        {aiAnalysisEnabled && (
          <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid var(--hair)' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 500, color: 'var(--txt)' }}>
              {language === 'ar' ? 'لغة التحليل' : 'Analysis Language'}
            </label>
            <select
              value={analysisLanguage}
              onChange={(e) => setAnalysisLanguage(e.target.value as 'en' | 'ar')}
              style={{
                width: '100%',
                padding: '6px 8px',
                borderRadius: '6px',
                border: '1px solid var(--hair)',
                backgroundColor: 'var(--base)',
                color: 'var(--txt)',
                fontSize: '13px',
                fontFamily: 'var(--font-ui)',
                cursor: 'pointer'
              }}
            >
              <option value="en">English</option>
              <option value="ar">العربية</option>
            </select>
            <p style={{ margin: '8px 0 0 0', fontSize: '11px', color: 'var(--muted-2)' }}>
              {language === 'ar'
                ? 'اختر اللغة لعرض تحليلات الذكاء الاصطناعي'
                : 'Select language for AI analysis display'}
            </p>
          </div>
        )}
      </div>

      {/* Current Watchlist */}
      <div style={{ marginBottom: '16px' }}>
        <h4 style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: 600, color: 'var(--txt)' }}>
          {language === 'ar' ? 'قائمة المراقبة الحالية' : 'Current Watchlist'}
        </h4>
        {watchlist.length === 0 ? (
          <p style={{ margin: 0, color: 'var(--muted)', fontSize: '13px' }}>
            {language === 'ar' ? 'لا توجد رموز في قائمة المراقبة' : 'No symbols in watchlist'}
          </p>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {watchlist.map(symbol => (
              <div
                key={symbol}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '4px 10px',
                  backgroundColor: 'var(--elev2)',
                  border: '1px solid var(--hair)',
                  borderRadius: '6px',
                  gap: '8px',
                  fontFamily: 'var(--font-mono)',
                  fontSize: '12.5px'
                }}
              >
                <span>{symbol}</span>
                <button
                  onClick={() => removeFromWatchlist(symbol)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--muted)',
                    cursor: 'pointer',
                    fontSize: '14px',
                    lineHeight: 1,
                    padding: '0 2px'
                  }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Symbol */}
      <div>
        <h4 style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: 600, color: 'var(--txt)' }}>
          {language === 'ar' ? 'إضافة رمز' : 'Add Symbol'}
        </h4>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={language === 'ar' ? 'ابحث عن رمز...' : 'Search for symbol...'}
          style={{
            width: '100%',
            padding: '6px 10px',
            marginBottom: '8px',
            borderRadius: '6px',
            border: '1px solid var(--hair)',
            backgroundColor: 'var(--base)',
            color: 'var(--txt)',
            fontSize: '13px',
            fontFamily: 'var(--font-ui)'
          }}
        />
        {filteredSymbols.length > 0 && (
          <div
            style={{
              maxHeight: '200px',
              overflowY: 'auto',
              border: '1px solid var(--hair)',
              borderRadius: '6px',
              backgroundColor: 'var(--base)'
            }}
          >
            {filteredSymbols.slice(0, 20).map(symbol => (
              <div
                key={symbol}
                onClick={() => handleAddSymbol(symbol)}
                style={{
                  padding: '6px 10px',
                  cursor: 'pointer',
                  borderBottom: '1px solid var(--hair)',
                  fontFamily: 'var(--font-mono)',
                  fontSize: '12.5px',
                  transition: 'background-color var(--dur-micro) var(--ease)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--mmec-row-hover)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                {symbol}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WatchlistManager;
